import type { LangPackStringValue } from '../../api/types';
import type { LangKey } from '../../types/language';

type LocalStrings = Partial<Record<LangKey, LangPackStringValue>>;

const en: LocalStrings = {
  ALongTimeAgo: 'last seen a long time ago',
  AccDescrGoBack: 'Go back',
  ActionCreatedChat: '{from} created the group «{title}»',
  ActionUserJoinedByLink: '{from} joined the group via invite link',
  AddContact: 'Add Contact',
  Back: 'Back',
  Block: 'Block',
  Cancel: 'Cancel',
  ChatMemberListNoAccess: 'You have no access to view the member list',
  Close: 'Close',
  Copy: 'Copy',
  DaysAgo: { one: '{count} day ago', other: '{count} days ago' },
  Done: 'Done',
  EventLogFilterPinnedMessages: 'Pinned Messages',
  FilterArchived: 'Archived',
  FilterChannels: 'Channels',
  FilterGroups: 'Groups',
  FilterMuted: 'Muted',
  FilterRead: 'Read',
  HoursAgo: { one: '{count} hour ago', other: '{count} hours ago' },
  JustNowAgo: 'just now',
  Lately: 'last seen recently',
  MinutesAgo: { one: '{count} minute ago', other: '{count} minutes ago' },
  NoMessages: 'No messages here yet...',
  Notifications: 'Notifications',
  Online: 'online',
  OnlineCount: { one: '{count} online', other: '{count} online' },
  PinnedMessageTitle: { one: 'Pinned Message #{index}', other: 'Pinned Message #{index}' },
  PinnedMessageTitleSingle: 'Pinned Message',
  PinnedMessagesCount: { one: 'Pinned Message', other: '{count} Pinned Messages' },
  ProfileJoinGroup: 'Join Group',
  ProfileTabMembers: 'Members',
  ProfileTabSubscribers: 'Subscribers',
  RevokeButton: 'Revoke',
  Save: 'Save',
  Search: 'Search',
  SelectChat: 'Select Chat',
  Send: 'Send',
  Subscribers: { one: '{count} subscriber', other: '{count} subscribers' },
  Unblock: 'Unblock',
  WithinAMonth: 'last seen within a month',
  WithinAWeek: 'last seen within a week',
  ErrorPhoneNumberInvalid: 'Invalid phone number, please try again',
  ErrorCodeInvalid: 'Invalid code, please try again',
  ErrorIncorrectPassword: 'Invalid password, please try again',
  ErrorPasswordFlood: 'Too many attempts, please try again later',
  ErrorPhoneBanned: 'This phone number is banned',
  ErrorFloodTime: 'Too many attempts, please try again in {time}',
  ErrorUnexpected: 'Unexpected error',
  ErrorUnexpectedMessage: 'Unexpected error: {error}',
};

const pt: LocalStrings = {
  ALongTimeAgo: 'visto há muito tempo',
  AccDescrGoBack: 'Voltar',
  ActionCreatedChat: '{from} criou o grupo «{title}»',
  ActionUserJoinedByLink: '{from} entrou no grupo pelo link de convite',
  AddContact: 'Adicionar contato',
  Back: 'Voltar',
  Block: 'Bloquear',
  Cancel: 'Cancelar',
  ChatMemberListNoAccess: 'Sem permissão para ver a lista de membros',
  Close: 'Fechar',
  Copy: 'Copiar',
  DaysAgo: { one: 'há {count} dia', other: 'há {count} dias' },
  Done: 'Concluído',
  EventLogFilterPinnedMessages: 'Mensagens fixadas',
  FilterArchived: 'Arquivados',
  FilterChannels: 'Canais',
  FilterGroups: 'Grupos',
  FilterMuted: 'Silenciados',
  FilterRead: 'Lidos',
  HoursAgo: { one: 'há {count} hora', other: 'há {count} horas' },
  JustNowAgo: 'agora mesmo',
  Lately: 'visto recentemente',
  MinutesAgo: { one: 'há {count} minuto', other: 'há {count} minutos' },
  NoMessages: 'Nenhuma mensagem aqui ainda...',
  Notifications: 'Notificações',
  Online: 'online',
  OnlineCount: { one: '{count} online', other: '{count} online' },
  PinnedMessageTitle: { one: 'Mensagem fixada #{index}', other: 'Mensagem fixada #{index}' },
  PinnedMessageTitleSingle: 'Mensagem fixada',
  PinnedMessagesCount: { one: 'Mensagem fixada', other: '{count} mensagens fixadas' },
  ProfileJoinGroup: 'Entrar no grupo',
  ProfileTabMembers: 'Membros',
  ProfileTabSubscribers: 'Inscritos',
  RevokeButton: 'Revogar',
  Save: 'Salvar',
  Search: 'Pesquisar',
  SelectChat: 'Selecionar conversa',
  Send: 'Enviar',
  Subscribers: { one: '{count} inscrito', other: '{count} inscritos' },
  Unblock: 'Desbloquear',
  WithinAMonth: 'visto no último mês',
  WithinAWeek: 'visto na última semana',
  ErrorPhoneNumberInvalid: 'Número de telefone inválido, tente novamente',
  ErrorCodeInvalid: 'Código inválido, tente novamente',
  ErrorIncorrectPassword: 'Senha inválida, tente novamente',
  ErrorPasswordFlood: 'Muitas tentativas, tente novamente mais tarde',
  ErrorPhoneBanned: 'Este número de telefone foi banido',
  ErrorFloodTime: 'Muitas tentativas, tente novamente em {time}',
  ErrorUnexpected: 'Erro inesperado',
  ErrorUnexpectedMessage: 'Erro inesperado: {error}',
  AccDescrOpenMenu2: 'Abrir menu',
  ActionAddUser: '{from} adicionou {user}',
  ActionAddUserYou: 'Você adicionou {user}',
  ActionAddUsersMany: '{from} adicionou {users}',
  ActionAddUsersManyYou: 'Você adicionou {users}',
  ActionAddYou: '{from} adicionou você a este canal',
  ActionAttachMenuBotAllowed: 'Você permitiu que este bot lhe envie mensagens ao adicioná-lo ao menu de anexos.',
  ActionBotAllowedFromDomain: 'Você permitiu que este bot lhe envie mensagens ao entrar em {domain}.',
  ActionChangedPhoto: '{from} atualizou a foto do grupo',
  ActionChangedPhotoChannel: 'Foto do canal atualizada',
  ActionChangedPhotoYou: 'Você atualizou a foto do grupo',
  ActionChangedTitle: '{from} alterou o nome do grupo para «{title}»',
  ActionChangedTitleChannel: 'O nome do canal foi alterado para «{title}»',
  ActionChangedTitleYou: 'Você alterou o nome do grupo para «{title}»',
  ActionChannelJoinedByRequestChannelYou: 'Seu pedido para entrar no canal foi aprovado',
  ActionChannelJoinedYou: 'Você entrou neste canal',
  ActionCreatedChannel: 'Canal criado',
  ActionExpiredVideo: 'Mensagem de vídeo expirada',
  ActionExpiredVoice: 'Mensagem de voz expirada',
  ActionFallbackChat: 'Conversa',
  ActionFallbackUser: 'Usuário',
  ActionHistoryCleared: 'O histórico foi limpo',
  ActionJoinedByRequest: '{from} foi aceito no grupo',
  ActionJoinedByRequestYou: 'Seu pedido para entrar no grupo foi aprovado',
  ActionKickUser: '{from} removeu {user}',
  ActionKickUserYou: 'Você removeu {user}',
  ActionMigratedFrom: 'Migrado de {chat}',
  ActionMigratedTo: 'Migrado para {chat}',
  ActionPinnedMedia: '{from} fixou {media}',
  ActionPinnedMediaAlbum: 'um álbum',
  ActionPinnedMediaAudio: 'um arquivo de áudio',
  ActionPinnedMediaContact: 'um contato',
  ActionPinnedMediaFile: 'um arquivo',
  ActionPinnedMediaGame: 'o jogo «{game}»',
  ActionPinnedMediaGif: 'um GIF',
  ActionPinnedMediaGiveaway: 'um sorteio',
  ActionPinnedMediaGiveawayResults: 'os resultados do sorteio',
  ActionPinnedMediaInvoice: 'uma fatura',
  ActionPinnedMediaLocation: 'uma localização',
  ActionPinnedMediaPhoto: 'uma foto',
  ActionPinnedMediaPoll: 'uma enquete',
  ActionPinnedMediaSticker: 'uma figurinha',
  ActionPinnedMediaStory: 'um story',
  ActionPinnedMediaVideo: 'um vídeo',
  ActionPinnedMediaVideoMessage: 'uma mensagem de vídeo',
  ActionPinnedMediaVoice: 'uma mensagem de voz',
  ActionPinnedMediaYou: 'Você fixou {media}',
  ActionPinnedNotFound: '{from} fixou uma mensagem',
  ActionPinnedNotFoundYou: 'Você fixou uma mensagem',
  ActionPinnedText: '{from} fixou "{text}"',
  ActionPinnedTextYou: 'Você fixou "{text}"',
  ActionRemovedPhoto: '{from} removeu a foto do grupo',
  ActionRemovedPhotoChannel: 'Foto do canal removida',
  ActionRemovedPhotoYou: 'Você removeu a foto do grupo',
  ActionScreenshotTaken: '{from} fez uma captura de tela!',
  ActionScreenshotTakenYou: 'Você fez uma captura de tela!',
  ActionUnsupported: 'Mensagem não suportada',
  ActionUserJoined: '{from} entrou no grupo',
  ActionUserJoinedYou: 'Você entrou no grupo',
  ActionUserLeft: '{from} saiu do grupo',
  ActionUserLeftYou: 'Você saiu do grupo',
  ActionUserRegistered: '{from} acabou de se cadastrar',
  ActionWebappBotAllowed: 'Você permitiu que este bot lhe envie mensagens no web app dele.',
  ActionWebviewDataDone: 'Os dados do botão "{text}" foram enviados ao bot.',
  AriaSearchNewerResult: 'Ir para o resultado anterior',
  AriaSearchOlderResult: 'Ir para o próximo resultado',
  AtDateAgo: '{date}',
  BotInfoTitle: 'O que este bot pode fazer?',
  ChatGroups: { one: '{count} grupo', other: '{count} grupos' },
  ChatNonContactUserGroups: 'Grupos em comum',
  ChatNonContactUserSubtitle: 'Não é um contato',
  ContactInfoNotVerified: 'Não é uma conta oficial',
  ContactInfoRegistration: 'Cadastro',
  CropperApply: 'Recortar imagem',
  CropperTitle: 'Arraste para reposicionar',
  Delete: 'Excluir',
  DiscussionStarted: 'Discussão iniciada',
  FilterAllChats: 'Todas as conversas',
  FilterAllChatsShort: 'Todas',
  FilterExclude: 'Conversas excluídas',
  FilterInclude: 'Conversas incluídas',
  FilterNew: 'Nova pasta',
  Filters: 'Pastas',
  FromYou: 'Você',
  ImportedInfo: 'Esta mensagem foi importada de outro app. Não podemos garantir que seja verdadeira.',
  Loading: 'Carregando...',
  MessageMetaApproximate: 'aprox.',
  MessageMetaImported: 'importada',
  MessageScheduledOn: 'Agendada para {date}',
  MessageScheduledUntilOnline: 'Agendada para quando ficar online',
  MessageSentPaidToastText: 'Você pagou {amount}',
  MessageSentPaidToastTitle: { one: 'Mensagem enviada!', other: '{count} mensagens enviadas!' },
  MessageTooltipEditedDate: 'Editada: {date}',
  MessageTooltipForwardedDate: 'Original: {date}',
  NewMessageTitle: 'Nova mensagem',
  OpenUrlConfirm: 'Abrir',
  OpenUrlText: 'Deseja abrir **{url}**?',
  OpenUrlTitle: 'Abrir link',
  PrivacyPhone: 'Número de telefone',
  ReplyInPrivateMessage: 'Responder em mensagem privada',
  SavedMessages: 'Mensagens salvas',
  SearchFriends: 'Pesquisar contatos',
  ServiceNotifications: 'notificações do serviço',
  StarsAmountText: { one: '{amount} Estrela', other: '{amount} Estrelas' },
  StoryTooltipReactionSent: 'Reação enviada!',
  StoryTooltipStickerSent: 'Figurinha enviada!',
  TextCopied: 'Texto copiado para a área de transferência.',
  UserUpdatedName: '{user} atualizou o nome {time}',
  UserUpdatedPhoto: '{user} atualizou a foto {time}',
};

export default { en, pt } as const;
